import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  createOffer,
  deleteOffer,
  listOffers,
} from "../../actions/offerActions";
import { watchPresenceStats } from "../../actions/presence";
import ActionsMenu from "../../components/AdminList/ActionsMenu";
import AdminList from "../../components/AdminList/AdminList";
import { MainContainer } from "../MainContainer";

const EMPTY_LIST = Object.freeze({
  items: Object.freeze([]),
  total: 0,
  loading: false,
});

const STATUS_LABELS = {
  draft: "Черновик",
  final: "PDF сформирован",
  sent: "Отправлено",
  won: "Согласовано",
  lost: "Отклонено",
};

const OffersAdminList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth?.user?._id);
  const list = useSelector(
    (state) => state.offers?.list || EMPTY_LIST
  );
  const presenceStats =
    useSelector((state) => state.presence?.stats) || {};

  const { items, loading, total } = list;
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    dispatch(listOffers({ q: query, status }));
  }, [query, status, dispatch]);

  useEffect(() => {
    const ids = (items || []).map((offer) => offer._id);
    if (!ids.length) return undefined;

    const unsubscribe = dispatch(
      watchPresenceStats({ roomIds: ids, prefix: "offer:" })
    );

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [items, dispatch]);

  const open = (id) => navigate(`/admin/offers/${id}`);

  const remove = async (id) => {
    if (!window.confirm("Удалить коммерческое предложение?")) return;

    await dispatch(deleteOffer(id));
    dispatch(listOffers({ q: query, status }));
  };

  const createDraft = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const payload = {
      schemaVersion: 3,
      creator: userId,
      company: "ALOTB.RU",
      title: "Коммерческое предложение",
      date: today,
      client: { name: "" },
      currency: "RUB",
      description: "",
      meta: {
        delivery: "",
        payment: "",
        warranty: "12 месяцев",
        validity: "14 календарных дней",
        extra: "",
        documentHeading: "КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ",
        documentDateText: "",
        showRecipient: false,
        recipientLabel: "Кому",
        recipientText: "",
        showSender: false,
        senderLabel: "От",
        senderText: "",
        showSignature: false,
        signatureLabel: "Подпись",
        signaturePosition: "",
        signatureName: "",
      },
      items: [],
      totals: {},
    };

    const document = await dispatch(createOffer(payload));
    if (document?._id) open(document._id);
  };

  const toolbar = {
    search: {
      value: query,
      onChange: setQuery,
      placeholder: "Поиск по номеру, названию, компании или клиенту",
    },
    filters: [
      {
        value: status,
        onChange: setStatus,
        options: [
          { value: "", label: "Все статусы" },
          { value: "draft", label: "Черновики" },
          { value: "final", label: "PDF сформирован" },
          { value: "sent", label: "Отправлено" },
          { value: "won", label: "Согласовано" },
          { value: "lost", label: "Отклонено" },
        ],
      },
    ],
    actions: [
      {
        label: "+ Новое КП",
        onClick: createDraft,
        variant: "primary",
      },
    ],
  };

  const columns = [
    {
      key: "num",
      title: "№",
      cell: (_offer, index) => index + 1,
      headerClassName: "col col--num",
      className: "col col--num",
    },
    {
      key: "title",
      title: "Название",
      cell: (offer) => (
        <button
          type="button"
          className="offers-admin__title-button"
          onClick={() => open(offer._id)}
        >
          <strong>{offer.title || "Без названия"}</strong>
          <span>{offer.docNumber ? `№ ${offer.docNumber}` : "Номер не указан"}</span>
        </button>
      ),
    },
    {
      key: "company",
      title: "Исполнитель",
      cell: (offer) => offer.company || "—",
    },
    {
      key: "client",
      title: "Заказчик",
      cell: (offer) => offer.client?.name || "Без адресата",
    },
    {
      key: "date",
      title: "Дата",
      cell: (offer) => offer.date || "—",
    },
    {
      key: "status",
      title: "Статус",
      cell: (offer) => (
        <div className="offers-admin__status-wrap">
          <span className={`offers-admin__status offers-admin__status--${offer.status || "draft"}`}>
            {STATUS_LABELS[offer.status] || offer.status || "Черновик"}
          </span>
          <span className="offers-admin__presence">
            {presenceStats[`offer:${offer._id}`]?.count || 0} онлайн
          </span>
        </div>
      ),
    },
    {
      key: "__actions",
      title: "Действия",
      className: "col col--actions",
      headerClassName: "col col--actions",
      cell: (offer) => (
        <ActionsMenu
          items={[
            {
              label: "Открыть редактор",
              onClick: () => open(offer._id),
            },
            { type: "sep" },
            {
              label: "Удалить",
              variant: "danger",
              onClick: () => remove(offer._id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <MainContainer
      backButtonProduct
      type="minimalFunctional"
      href="/"
      title="Коммерческие предложения"
    >
      <div className="offers-admin">
        <div className="offers-admin__intro">
          <div>
            <span>Документы</span>
            <h2>Коммерческие предложения</h2>
            <p>
              Создание, совместное редактирование и формирование PDF-версий.
            </p>
          </div>
          <strong>{total}</strong>
        </div>

        <AdminList
          toolbar={toolbar}
          columns={columns}
          items={items}
          loading={Boolean(loading)}
          emptyText="Коммерческие предложения не найдены"
          totalText={`Найдено документов: ${total}`}
          getRowKey={(offer) => offer._id}
          cols="60px 1.4fr 1fr 1fr 120px 170px 140px"
          colsMd="50px 1.3fr 1fr 1fr 110px 160px 130px"
          colsSm="1fr"
          theme="dark"
        />
      </div>
    </MainContainer>
  );
};

export default OffersAdminList;
